import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from '@/lib/google-maps/libraries';
import { format } from 'date-fns';
import {
  Loader2,
  MapPin,
  Clock,
  User,
  Phone,
  CheckCircle,
  AlertCircle,
  Navigation,
} from 'lucide-react';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { PublicLayout } from '@/components/public/PublicLayout';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { publicGet } from '@/lib/api/publicClient';
import { type PublicTracking } from '@/types/public';
import { dateStringToDate, formatDateTimeInTimeZone } from '@/lib/date-values';

export const Route = createFileRoute('/(public)/tracking/$id/$refno')({
  component: TrackingPage,
});

const MAP_CONTAINER_STYLE = { width: '100%', height: '320px' };
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };

const STATUS_INFO: Record<
  PublicTracking['status'],
  { label: string; icon: typeof CheckCircle; className: string }
> = {
  scheduled: { label: 'Scheduled', icon: Clock, className: 'bg-blue-100 text-blue-700' },
  en_route: { label: 'On the Way', icon: Navigation, className: 'bg-yellow-100 text-yellow-700' },
  on_site: { label: 'On Site', icon: MapPin, className: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', icon: CheckCircle, className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', icon: AlertCircle, className: 'bg-red-100 text-red-700' },
};

function TrackingPage() {
  const { id, refno } = Route.useParams();
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

  const {
    data: tracking,
    isLoading,
    isError,
  } = useQuery<PublicTracking>({
    queryKey: ['public', 'tracking', id, refno],
    queryFn: () => publicGet<PublicTracking>(`/api/public/tracking/${id}`, { refno }),
    refetchInterval: 30_000, // Poll every 30 seconds
    retry: false,
  });

  const { isLoaded: mapLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !tracking) {
    return <DocumentNotFound message="Tracking information not found" />;
  }

  const statusInfo = STATUS_INFO[tracking.status];
  const StatusIcon = statusInfo.icon;
  const techCenter = tracking.techLocation
    ? { lat: tracking.techLocation.lat, lng: tracking.techLocation.lng }
    : null;
  const jobCenter =
    tracking.jobAddress?.lat && tracking.jobAddress.lng
      ? { lat: tracking.jobAddress.lat, lng: tracking.jobAddress.lng }
      : null;
  const mapCenter = techCenter ?? jobCenter ?? DEFAULT_CENTER;

  return (
    <PublicLayout>
      <div className="space-y-4">
        {/* Status card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                <StatusIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900">Job #{refno}</p>
                  <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
                </div>
                {tracking.serviceType && (
                  <p className="text-sm text-gray-500">{tracking.serviceType}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule info */}
        {(tracking.scheduledDate || tracking.estimatedArrival) && (
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-3 text-sm">
                {tracking.scheduledDate && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Scheduled</p>
                      <p className="font-medium text-gray-900">
                        {format(dateStringToDate(tracking.scheduledDate)!, 'EEEE, MMM d, yyyy')}
                        {tracking.scheduledStart && (
                          <span className="text-gray-600">
                            {' '}
                            · {tracking.scheduledStart}
                            {tracking.scheduledEnd && ` – ${tracking.scheduledEnd}`}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                {tracking.estimatedArrival && (
                  <div className="flex items-center gap-3">
                    <Navigation className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-gray-500">Estimated Arrival</p>
                      <p className="font-medium text-gray-900">{tracking.estimatedArrival}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Technician info */}
        {tracking.technician && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                {tracking.technician.avatar ? (
                  <img
                    src={tracking.technician.avatar}
                    alt={tracking.technician.name}
                    className="h-10 w-10 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{tracking.technician.name}</p>
                  <p className="text-xs text-gray-500">Technician</p>
                </div>
                {tracking.technician.phone && (
                  <a
                    href={`tel:${tracking.technician.phone}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100"
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Map / no-location message */}
        {(tracking.status === 'en_route' || tracking.status === 'on_site') &&
          (!tracking.techLocation ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <Navigation className="h-8 w-8 text-gray-300" />
                  <p className="font-medium text-gray-700">Tech not yet dispatched</p>
                  <p className="text-sm text-gray-400">
                    Technician location not yet available. Check back soon.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : apiKey ? (
            <Card className="overflow-hidden">
              <div className="px-4 pb-2 pt-4">
                <p className="text-sm font-medium text-gray-700">
                  {tracking.status === 'en_route' ? 'Technician Location' : 'Job Location'}
                </p>
                <p className="text-xs text-gray-400">
                  {/* The public tracking endpoint (legacy monolith) should send `timezone`;
                      until it does, this falls back to the viewer tz. */}
                  Updated{' '}
                  {formatDateTimeInTimeZone(
                    tracking.techLocation.updatedAt,
                    tracking.timezone ?? undefined,
                    {
                      hour: 'numeric',
                      minute: '2-digit',
                    },
                  )}
                </p>
              </div>
              {mapLoaded ? (
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={mapCenter}
                  zoom={14}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    streetViewControl: false,
                  }}
                >
                  {techCenter && (
                    <Marker
                      position={techCenter}
                      title="Technician"
                      label={{ text: 'T', color: 'white', fontWeight: 'bold' }}
                    />
                  )}
                  {jobCenter && <Marker position={jobCenter} title="Job Location" />}
                </GoogleMap>
              ) : (
                <div className="flex h-80 items-center justify-center bg-gray-50">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              )}
            </Card>
          ) : null)}

        {/* Job address */}
        {tracking.jobAddress?.street && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-gray-500">Service Address</p>
                  <p className="font-medium text-gray-900">
                    {tracking.jobAddress.street}
                    {tracking.jobAddress.city && `, ${tracking.jobAddress.city}`}
                    {tracking.jobAddress.state && `, ${tracking.jobAddress.state}`}
                    {tracking.jobAddress.zip && ` ${tracking.jobAddress.zip}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {tracking.status === 'completed' && (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-green-50 py-4 text-green-700">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Job completed. Thank you for your business!</span>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
